/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/* global Config nSdkInstance */
// @flow

import moment from 'moment';
import { isLiveLinearSourceType } from 'Common/models/franchise';
import type { Store } from 'redux';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import BaseCastAnalytics from './BaseCastAnalytics';
import {
  MILLIS_IN_SECOND,
  AD_BREAK_TYPE_PREROLL,
  AD_BREAK_TYPE_MIDROLL,
  AD_BREAK_TYPE_POSTROLL,
  EMPTY_STRING,
} from './AnalyticsConstants';

declare var nSdkInstance: Object;

const TAG = 'NIELSEN[CAST]';

class NielsenCastAnalytics extends BaseCastAnalytics {
  enableNielsenDebug: boolean;
  lastPlayhead: number;
  lastAdPlayhead: number;
  сontentTimeChangedHeartbeat: IntervalID | void;
  adTimeChangedHeartbeat: IntervalID | void;
  isContentPlaying: boolean;
  isAdPlaying: boolean;
  isNewSenderConnected: boolean;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    super(playerManager, store);
    this.lastPlayhead = 0;
    this.lastAdPlayhead = 0;
    this.isContentPlaying = false;
    this.isAdPlaying = false;
    this.isNewSenderConnected = false;
    this.emptyValue = EMPTY_STRING;
  }

  get enabled() {
    return !!nSdkInstance && this.customAttributes?.nielsen_analytics_enabled;
  }

  onCafEvent(event: Object) {
    this.logAnalytics(TAG, 'Player event: ', event.type, event);
    switch (event.type) {
      case 'PLAYER_LOADING':
        this.logAnalytics(TAG, 'analyticsData = ', this.analyticsData);
        this.logAnalytics(TAG, " >>>>>  nSdkInstance.ggPM('loadMetadata', content");
        nSdkInstance.ggPM('loadMetadata', this._createMetadataObject());
        this._initialize();
        break;
      case 'BUFFERING': {
        this.isBuffering = event.isBuffering;
        this.logAnalytics(TAG, ' >>>>> ', 'isBuffering = ', this.isBuffering);
        break;
      }
      case 'PLAYING':
        if (!this.isAdPlaying) {
          this.logAnalytics(TAG, " >>>>>  nSdkInstance.ggPM('loadMetadata', content");
          nSdkInstance.ggPM('loadMetadata', this._createMetadataObject());
          this.isContentPlaying = true;
        }
        break;
      case 'PAUSE':
        this.isContentPlaying = false;
        this.logAnalytics(TAG, " >>>>>  nSdkInstance.ggPM('stop', this.lastAdPlayhead)");
        nSdkInstance.ggPM('stop', this.lastAdPlayhead);
        break;
      case 'ID3':
        this.logAnalytics(TAG, " >>>>> 'sendID3', event.segmentData = ", event.segmentData);
        nSdkInstance.ggPM('sendID3', event.segmentData);
        break;
      case 'BREAK_STARTED':
        this.isAdPlaying = true;
        this.logAnalytics(TAG, ' >>>>> ', 'isOndemandAdPlaying = ', this.isAdPlaying);
        break;
      case 'BREAK_ENDED':
        this.isAdPlaying = false;
        this.logAnalytics(TAG, ' >>>>> ', 'isOndemandAdPlaying = ', this.isAdPlaying);
        break;
      case 'BREAK_CLIP_STARTED': {
        this.logAnalytics(TAG, " >>>>>  nSdkInstance.ggPM('loadMetadata', ad)");
        nSdkInstance.ggPM('loadMetadata', this._createAdMetadataObject(event));
        this.lastAdPlayhead = 0;
        this.adTimeChangedHeartbeat = setInterval(() => {
          if (!this.isBuffering) {
            nSdkInstance.ggPM('setPlayheadPosition', this.lastAdPlayhead);
            this.logAnalytics(
              TAG,
              ' >>>>> Ad position = ',
              this.lastAdPlayhead,
              '  breakClipCurrentTimeSec = ',
              this.breakManager.getBreakClipCurrentTimeSec(),
            );
            this.lastAdPlayhead += 1;
          }
        }, 1000);
        break;
      }
      case 'BREAK_CLIP_ENDED':
        this.logAnalytics(TAG, " >>>>>  nSdkInstance.ggPM('stop', this.lastAdPlayhead)");
        nSdkInstance.ggPM('stop', this.lastAdPlayhead);
        clearInterval(this.adTimeChangedHeartbeat);
        this.adTimeChangedHeartbeat = undefined;
        break;
      default:
        event.handleInSuper = true;
        break;
    }
    return event;
  }

  onCafShutdown() {
    this.logAnalytics(TAG, ' >>>>> ', 'onCafShutdown()');
    nSdkInstance.ggPM('end', this.lastPlayhead);
    const updateOttDisconnected = JSON.stringify({ ottStatus: '0' });
    this.logAnalytics(TAG, ' >>>>> ', `nSdkInstance.ggPM(updateOTT, ${updateOttDisconnected})`);
    nSdkInstance.ggPM('updateOTT', updateOttDisconnected);
  }

  onSenderConnected(sender: Object) {
    this.logAnalytics(
      TAG,
      ' >>>>> ',
      'onSenderConnected()  senderId = ',
      sender.senderId,
      'userAgent = ',
      sender.userAgent,
    );
    this.isNewSenderConnected = true;
  }

  logUpdateOtt(
    nielsenDemographicId: string,
    nielsenOptOutStatus: boolean,
    deviceId: string,
    deviceVersion: string,
    deviceFriendlyName: string,
    deviceModelName: string,
  ) {
    if (this.isNewSenderConnected) {
      const ottInfo = {
        ottStatus: '1',
        ottType: 'casting',
        ottDevice: deviceModelName || this.emptyValue,
        ottDeviceName: deviceFriendlyName || this.emptyValue,
        ottDeviceID: deviceId || this.emptyValue,
        ottDeviceManufacturer: deviceModelName || this.emptyValue,
        ottDeviceModel: deviceModelName || this.emptyValue,
        ottDeviceVersion: deviceVersion || this.emptyValue,
        kGCKMetadataNlsKeyOptout: nielsenOptOutStatus || false,
        kGCKMetadataNlsKeyDeviceID: nielsenDemographicId || this.emptyValue,
      };
      this.logAnalytics(TAG, ' >>>>> ', 'logUpdateOtt() ottInfo = ', ottInfo);
      nSdkInstance.ggPM('updateOTT', JSON.stringify(ottInfo));
      this.isNewSenderConnected = false;
    }
  }

  onPlaybackFinished() {
    this.logAnalytics(TAG, ' >>>>>  ', 'End');
    this.logAnalytics(TAG, ` >>>>>  nSdkInstance.ggPM('end', ${this.lastPlayhead})`);
    nSdkInstance.ggPM('end', this.lastPlayhead);
    clearInterval(this.adTimeChangedHeartbeat);
    this.adTimeChangedHeartbeat = undefined;
    clearInterval(this.сontentTimeChangedHeartbeat);
    this.сontentTimeChangedHeartbeat = undefined;
  }

  _initialize() {
    this.logAnalytics(TAG, 'initialize()');
    if (!this.сontentTimeChangedHeartbeat) {
      this.сontentTimeChangedHeartbeat = setInterval(() => {
        if (!this.isAdPlaying && !this.isBuffering && this.isContentPlaying) {
          this.lastPlayhead = Math.floor(this.playerManager.getCurrentTimeSec());
          nSdkInstance.ggPM('setPlayheadPosition', this.lastPlayhead);
          this.logAnalytics(TAG, ' >>>>> ', 'onTimeChanged() => position = ', this.lastPlayhead);
        } else {
          this.logAnalytics(
            TAG,
            ' >>>>> ',
            'onTimeChanged() => isAd = ',
            this.isAdPlaying,
            '   isBuff = ',
            this.isBuffering,
            '  isContentPlaying = ',
            this.isContentPlaying,
          );
        }
      }, 1000);
      this.isInitialized = true;
    }
  }

  _createMetadataObject() {
    this.logAnalytics(TAG, ' >>>>> ', 'createMetadataObject()');
    let metadataObject;
    // DTVR
    if (isLiveLinearSourceType(this.sourceType)) {
      const channelName = this.channelName || this.networkName || this.ottMapId;
      metadataObject = {
        type: 'content',
        channelName,
        adModel: '2', // DAI
      };
      // DCR
    } else {
      const assetId = this.videoId;
      const franchiseName = this.showName;
      const episodeTitle = this.isCompleteEpisode ? this.showName : this.episodeTitle;
      const duration = this.contentLength;
      const airdate = this._getAirDate();
      const crossId1 = this.episodeId || this.contentId;

      metadataObject = {
        type: 'content',
        assetid: assetId,
        program: franchiseName,
        title: episodeTitle,
        length: duration,
        airdate,
        isfullepisode: 'y',
        adloadtype: 2, // DCR ad type
        crossId1,
        crossId2: this.networkName,
      };
    }
    this.logAnalytics(TAG, ' metadataObject = ', metadataObject);

    return metadataObject;
  }

  _createAdMetadataObject(event: Object) {
    this.logAnalytics(TAG, ' >>>>> ', '_createAdMetadataObject()');
    const currentAdId = event.breakId;
    const currentAdType = this._getAdBreakType(currentAdId);
    this.logAnalytics(TAG, ' >>>>>  AdId = ', currentAdId, '  AdType = ', currentAdType);
    return {
      assetid: currentAdId,
      type: currentAdType,
    };
  }

  _getAirDate() {
    const { release_year: releaseDate } = this.eventFranchiseData;
    const date = isLiveLinearSourceType(this.sourceType)
      ? this.analyticsData?.originalAirDate
      : String(releaseDate || 0);
    return moment(date, 'YYYYMMDD').format('YYYYMMDD hh:mm:ss');
  }

  _getAdBreakType(adBreakId?: string) {
    let adBreakType = AD_BREAK_TYPE_MIDROLL.toLowerCase();
    const currentAdBreak = this.playerManager.getBreakManager().getBreakClipById(adBreakId);
    if (this.isOndemandOrEstSourceType(this.sourceType)) {
      if (currentAdBreak && currentAdBreak.title) {
        if (currentAdBreak.title.includes(AD_BREAK_TYPE_PREROLL)) {
          adBreakType = AD_BREAK_TYPE_PREROLL.toLowerCase();
        } else if (currentAdBreak.title.includes(AD_BREAK_TYPE_MIDROLL)) {
          adBreakType = AD_BREAK_TYPE_MIDROLL.toLowerCase();
        } else if (currentAdBreak.title.includes(AD_BREAK_TYPE_POSTROLL)) {
          adBreakType = AD_BREAK_TYPE_POSTROLL.toLowerCase();
        }
      }
    } else if (isLiveLinearSourceType(this.sourceType)) {
      adBreakType = AD_BREAK_TYPE_MIDROLL.toLowerCase();
    }
    return adBreakType;
  }
}

export default NielsenCastAnalytics;
